<template>
  <div id="clients" class="flex">
    <div class="flex flex-col flex-grow">
      <div
        class="sticky left-0 z-50 flex items-center justify-between h-20 gap-4 px-6 border-b top-18 bg-slate-200 border-slate-400"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <div class="flex flex-col gap-1">
          <div class="flex items-center gap-4">
            <BlTextTitle4 text="Clients" />
            <BlBubbleCount :count="totalClients || 0" />
          </div>
          <BlTextBody2 text="View and organize your list of clients." />
        </div>

        <div class="flex items-center gap-4">
          <BlInputButton
            v-if="currentUser && currentUser.role.includes('admin')"
            type="outlined"
            label="Assign therapist"
            @clicked="onOpenAssignTherapist"
          />
          <BlInputButton
            icon="plus"
            label="New client"
            @clicked="newClientOpened = true"
          />
        </div>
      </div>

      <NewClientModal
        v-if="newClientOpened"
        @close-modal="newClientOpened = false"
      />

      <BlBackdrop
        v-if="assignTherapistOpened"
        title="Assign therapist"
        description="Manage and assign clients to specific therapists."
        label="Update"
        :disabled="!verifiedAssignTherapistInput"
        :submitLoading="assignTherapistLoading"
        @backdrop-close="onCancelAssignTherapist"
        @backdrop-action="onAsignTherapist"
      >
        <div
          class="flex flex-col gap-4 px-4 lg:px-8"
          :style="{ width: '40rem' }"
        >
          <BlSearchSelect
            label="Client's name"
            asterisk
            v-model="assignTherapistInput.id"
            placeholder="Find your client's name"
            :fetchProperty="{
              url: `/api/v1/clients?sort=alphabetical&status=active&query=`,
              dataKey: 'clients',
              countKey: 'total_clients',
              optionKeys: { value: 'id', label: 'name' }
            }"
            @change="onChangeClient"
          />
          <BlSearchBox
            :key="`multi-select-assign-${assignTherapistInput.id}`"
            type="user"
            label="Assign to"
            v-model="assignTherapistInput.userIds"
            placeholder="Type your therapist's name"
            :options="
              assignTherapistInput.existingUsers.map(i => ({
                value: i.id,
                label: i.name
              }))
            "
            :disabled="!assignTherapistInput.id"
            :fetchProperty="{
              url: `/api/v1/users?sort=name_asc&status=active&query=`,
              dataKey: 'users',
              countKey: 'total_users',
              optionKeys: { value: 'id', label: 'name' }
            }"
            @change="assignTherapistInput.userIds = $event"
          />
          <div
            v-if="diffAssignTherapistCount > 0"
            class="flex items-start gap-2 px-3 py-2 text-sm rounded bg-pending-100 text-pending"
          >
            <BlIcon icon="warning" color="pending" class="flex-shrink-0 w-6" />
            <span class="flex-wrap">
              You are about to remove
              {{ diffAssignTherapistCount }} therapist(s) from this client.
              <br />
              Once removed, they will no longer have access to this client's
              information.
              <br />
              Don't worry, you can always assign them again in the future.
            </span>
          </div>
        </div>
      </BlBackdrop>

      <div
        class="sticky left-0 z-30 flex items-center gap-2 px-6 py-3 mb-3 border-b border-slate-400"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <BlInputSearch
          class="w-80"
          :input="searchInput"
          @changed="onInputSearch"
        />
        <div class="w-40">
          <BlInputFilter :input="filterInput" @changed="onChangeFilter" />
        </div>
        <BlInputSelect
          class="w-60"
          @changed="onChangeFilter"
          :input="sortInput"
        />
      </div>

      <div
        class="px-4 mb-4 bl-clients-body lg:mb-8 lg:px-6"
        :style="{ width: 'calc(100vw - 0.5rem)' }"
      >
        <div v-if="pageLoading" class="grid h-128 place-items-center">
          <div class="blum-loader"></div>
        </div>
        <div v-else-if="!clients.length" class="grid h-128 place-items-center">
          <BlTextBody1 text="Client not found." />
        </div>
        <div
          v-else
          class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        >
          <div v-for="client in clients" :key="client.id" class="bl-client">
            <router-link :to="`/clients/${client.id}`">
              <div class="bl-client-header">
                <div class="bl-client-header-avatar-container">
                  <Avatar :name="client.name" :status="client.status" />
                </div>
                <div class="bl-client-header-detail-container">
                  <div class="flex items-center gap-2 pr-4">
                    <BlTextSubtitle2
                      :text="client.name"
                      class="truncate bl-client-header-name"
                    />
                    <div v-if="client.is_new" class="ml-auto">
                      <BlTextBody2
                        class="px-3 py-1 rounded-full text-slate-100 bg-success-400"
                        text="New"
                      />
                    </div>
                  </div>
                  <div class="bl-client-header-birthday">
                    <BlIcon
                      icon="cake"
                      class="bl-client-header-birthday-icon"
                    />
                    <BlTextBody2
                      :text="getDateWithoutTimezone(client.birthday)"
                    />
                  </div>
                </div>
              </div>
              <div class="bl-client-body">
                <BlTextBody2
                  :text="
                    client.status === 'active'
                      ? client.note
                      : client.status === 'archived'
                      ? `Discharged on ${getDateWithoutTimezone(
                          client.archived_at
                        )}`
                      : 'At risk of discharge'
                  "
                  class="bl-client-note"
                />
              </div>
            </router-link>

            <div class="flex flex-col gap-4 bl-client-footer">
              <div class="flex flex-wrap gap-4">
                <div
                  v-for="access in client.accesses"
                  :key="access.id"
                  :style="{ maxWidth: '50%' }"
                >
                  <router-link
                    v-if="access.user_id && access.user && access.user.name"
                    :event="currentUser.role.includes('admin') ? 'click' : ''"
                    target="_blank"
                    :to="{
                      name: 'TeamSessionHistory',
                      params: {
                        user_id: access.user_id,
                        redirect_target: `/clients`,
                        redirect_label: 'Clients'
                      }
                    }"
                    class="flex flex-wrap items-center px-2 truncate border rounded h-7 bg-success-100 border-success-100"
                    :class="[
                      currentUser.role.includes('admin')
                        ? 'hover:border-success-400'
                        : 'cursor-default'
                    ]"
                    :title="access.user.name"
                  >
                    <BlTextBody2
                      class="truncate text-success"
                      :text="access.user.name"
                    />
                  </router-link>
                </div>
              </div>
              <div class="flex flex-wrap gap-4">
                <div
                  v-if="enableBranch"
                  v-for="branch in client.branches"
                  :key="branch.id"
                  class="flex items-center justify-center px-2 truncate rounded h-7"
                  :style="{
                    backgroundColor: branch.background_color || 'unset',
                    maxWidth: '50%'
                  }"
                >
                  <BlTextBody2
                    class="truncate"
                    :text="branch.name || '-'"
                    :style="{ color: branch.color || 'unset' }"
                  />
                </div>
                <div
                  v-for="tag in client.tags"
                  :key="tag.id"
                  class="flex items-center justify-center px-2 truncate rounded h-7 bg-slate-300"
                  :style="{ maxWidth: '50%' }"
                >
                  <BlTextBody2
                    class="truncate whitespace-no-wrap text-slate-800"
                    :text="tag.name || '-'"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="totalClients > 20" class="mb-4 lg:mb-8">
        <BlInputPagination
          :count="Math.ceil(totalClients / ($route.query['per_page'] || 20))"
          :page="page"
          @changed="changePage"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import debounce from "lodash/debounce";
import { getDateWithoutTimezone } from "@/plugins/helper.js";

import NewClientModal from "./client/NewClientModal.vue";

import BlTextTitle4 from "../BlTextTitle4.vue";
import BlTextSubtitle2 from "../BlTextSubtitle2.vue";
import BlTextBody1 from "../BlTextBody1.vue";
import BlTextBody2 from "../BlTextBody2.vue";
import Avatar from "../Avatar.vue";
import BlBubbleCount from "../BlBubbleCount.vue";
import BlIcon from "../BlIcon.vue";
import BlInputButton from "../BlInputButton.vue";
import BlInputSearch from "../BlInputSearch.vue";
import BlInputFilter from "../BlInputFilter.vue";
import BlInputSelect from "../BlInputSelect.vue";
import BlSearchBox from "../BlSearchBox.vue";
import BlSearchSelect from "../BlSearchSelect.vue";
import BlInputPagination from "../BlInputPagination.vue";
import BlBackdrop from "../BlBackdrop.vue";

export default {
  name: "Clients",
  components: {
    NewClientModal,
    //
    BlTextTitle4,
    BlTextSubtitle2,
    BlTextBody1,
    BlTextBody2,
    Avatar,
    BlBubbleCount,
    BlIcon,
    BlInputButton,
    BlInputSearch,
    BlInputFilter,
    BlInputSelect,
    BlSearchBox,
    BlSearchSelect,
    BlInputPagination,
    BlBackdrop
  },
  data() {
    const defaultFilter = [[], [], [], { start_date: "", end_date: "" }, []];
    return {
      query: "",
      queryTimeout: null,
      page: 1,
      pageLoading: false,
      searchInput: { placeholder: "Search by name", model: "" },
      defaultFilter,
      filterInput: {
        model: defaultFilter,
        tabs: [
          {
            name: "Status",
            kind: "multi-tag",
            options: [
              {
                name: "Active",
                value: "active",
                color: "#789719",
                backgroundColor: "#F2F8CF"
              },
              {
                name: "Discharged",
                value: "archived",
                color: "#F63D68",
                backgroundColor: "#FFF1F3"
              },
              {
                name: "At risk of discharge",
                value: "at_risk_of_discharge",
                color: "#FB6514",
                backgroundColor: "#FFF6ED"
              }
            ]
          },
          {
            name: "Therapist",
            kind: "multi-tag",
            options: [],
            url: `/api/v1/users?sort=name_asc&status=active&query=`,
            fetchPage: 1,
            dataKey: "users",
            countKey: "total_users",
            optionKeys: {
              value: "id",
              label: "name",
              color: "#4B810E",
              backgroundColor: "#F2FFE3"
            }
          },
          {
            name: "Tags",
            kind: "multi-tag",
            options: [],
            url: `/api/v1/labels?sort=name_asc&query=`,
            fetchPage: 1,
            dataKey: "labels",
            countKey: "total_count",
            optionKeys: {
              value: "id",
              label: "name",
              color: "#475467",
              backgroundColor: "#F2F4F7"
            }
          },
          { name: "Date of birth", kind: "date-custom" }
        ]
      },
      sortInput: {
        placeholder: "Sort",
        model: { name: "Recently created", value: "most_recent" },
        options: [
          { name: "Recently created", value: "most_recent" },
          { name: "Date of Birth: Descending", value: "birthday_desc" },
          { name: "Date of Birth: Ascending", value: "birthday_asc" },
          { name: "Alphabetical Order (A-Z)", value: "alphabetical" }
        ]
      },
      //
      assignTherapistOpened: false,
      assignTherapistLoading: false,
      assignTherapistInput: {
        id: "",
        userIds: [],
        existingUserIds: [],
        existingUsers: []
      },
      //
      newClientOpened: false
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "users",
      "clients",
      "totalClients"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    getParams() {
      const searchParams = this.$route.query;
      let params = `?page=${this.page}&per_page=${searchParams["per_page"] ||
        20}`;

      const query = this.searchInput.model || "";
      const statuses = this.filterInput.model.at(0) || [];
      const therapists = this.filterInput.model.at(1) || [];
      const labels = this.filterInput.model.at(2) || [];
      const dates = this.filterInput.model.at(3) || {
        start_date: "",
        end_date: ""
      };
      const branches = this.filterInput.model.at(4) || [];
      const sort = this.sortInput.model || { value: "most_recent" };

      if (query) {
        params += `&query=${this.searchInput.model}`;
      }
      if (statuses.length > 0) {
        params += `&status=${statuses.map(a => a.value).join(",")}`;
      }
      if (therapists.length > 0) {
        params += `&therapist_ids=${therapists.map(a => a.value).join(",")}`;
      }
      if (labels.length > 0) {
        params += `&tag_label_ids=${labels.map(a => a.value).join(",")}`;
      }
      if (dates.start_date) {
        params += `&start_date_of_birth=${dates.start_date}`;
      }
      if (dates.end_date) {
        params += `&end_date_of_birth=${dates.end_date}`;
      }
      if (branches.length > 0) {
        params += `&branch_ids=${branches.map(a => a.value).join(",")}`;
      }
      if (sort.value !== "most_recent") {
        params += `&sort=${sort.value}`;
      }

      return params;
    },
    diffAssignTherapistCount() {
      const existingUserIds = this.assignTherapistInput.existingUserIds;
      const userIds = this.assignTherapistInput.userIds;
      return this.differenceTwoArray(existingUserIds, userIds).length;
    },
    verifiedAssignTherapistInput() {
      if (!this.assignTherapistInput.id) return false;
      const userIds = this.assignTherapistInput.userIds;
      const existingUserIds = this.assignTherapistInput.existingUserIds;
      const ab = this.differenceTwoArray(userIds, existingUserIds);
      const ba = this.differenceTwoArray(existingUserIds, userIds);
      if (!ab.length && !ba.length) return false;
      return true;
    }
  },
  watch: {
    query() {
      clearTimeout(this.queryTimeout);
      this.queryTimeout = setTimeout(() => {
        this.fetchClients();
      }, 1000);
    }
  },
  created() {},
  mounted() {
    const searchParams = this.$route.query;

    if (searchParams["page"]) {
      this.page = Number(searchParams["page"]);
    }
    if (searchParams["query"]) {
      this.searchInput.model = searchParams["query"];
    }

    if (this.enableBranch) {
      const tab = {
        name: "Branches",
        kind: "multi-tag",
        options: [],
        url: `/api/v1/branches?sort=name_asc&query=`,
        fetchPage: 1,
        dataKey: "branches",
        countKey: "total_count",
        optionKeys: {
          value: "id",
          label: "name",
          color: "color",
          backgroundColor: "background_color"
        }
      };
      this.filterInput.tabs = [...this.filterInput.tabs, tab];
    }

    let filter = [[], [], [], { start_date: "", end_date: "" }, []];
    if (searchParams["status"]) {
      const arr = searchParams["status"].split(",");
      filter[0] = arr.map(i => ({ value: i }));
    }
    if (searchParams["therapist_ids"]) {
      const arr = searchParams["therapist_ids"].split(",");
      filter[1] = arr.map(i => ({ value: Number(i) }));
    }
    if (searchParams["tag_label_ids"]) {
      const arr = searchParams["tag_label_ids"].split(",");
      filter[2] = arr.map(i => ({ value: Number(i) }));
    }
    if (searchParams["start_date_of_birth"]) {
      filter[3].start_date = searchParams["start_date_of_birth"];
    }
    if (searchParams["end_date_of_birth"]) {
      filter[3].end_date = searchParams["end_date_of_birth"];
    }
    if (searchParams["branch_ids"]) {
      const arr = searchParams["branch_ids"].split(",");
      filter[4] = arr.map(i => ({ value: Number(i) }));
    }
    this.filterInput.model = filter;
    if (searchParams["sort"]) {
      const sort = this.sortInput.options.find(
        i => i.value === searchParams["sort"]
      );
      this.sortInput.model = sort;
    }

    this.fetchClients();
  },
  destroyed() {
    const app = document.getElementById("app");
    if (app) app.scroll({ top: 0, left: 0 });
  },
  methods: {
    ...mapActions(["actionGet", "getClients", "updateClient"]),
    getDateWithoutTimezone,
    inputHandler(payload) {
      payload.input.model = payload.value;
    },
    //
    fetchClients() {
      this.pageLoading = true;
      const params = this.getParams;
      this.getClients({ params }).then(() => {
        this.pageLoading = false;
      });

      // sync url path for page
      const incomingPath = "/clients" + params;
      if (this.$route.fullPath !== incomingPath) {
        return this.$router.push(incomingPath);
      }
    },
    //
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.page = 1;
      this.fetchClients();
    }, 1000),
    onChangeFilter(payload) {
      payload.input.model = payload.value;
      this.page = 1;
      this.fetchClients();
    },
    changePage(page) {
      this.page = page;
      this.fetchClients();
    },
    // assign therapist
    onOpenAssignTherapist() {
      this.assignTherapistOpened = true;
      this.resetAssignTherapist();
    },
    onCancelAssignTherapist() {
      this.assignTherapistOpened = false;
    },
    resetAssignTherapist() {
      this.assignTherapistInput = {
        id: "",
        userIds: [],
        existingUserIds: [],
        existingUsers: []
      };
    },
    differenceTwoArray(a = [], b = []) {
      return a.filter(i => !b.includes(i));
    },
    async onChangeClient(id) {
      if (!id) {
        this.resetAssignTherapist();
        return;
      }
      const params = { url: `/api/v1/clients/${id}` };
      const { success, data: client } = await this.actionGet(params);
      if (!success) return;

      const users = client.accesses.map(i => i.user);
      const ids = client.accesses.map(i => Number(i.user_id));
      this.assignTherapistInput = {
        id: client.id,
        userIds: ids,
        existingUserIds: ids,
        existingUsers: users
      };
    },
    async onAsignTherapist() {
      this.assignTherapistLoading = true;
      const { success } = await this.updateClient({
        id: this.assignTherapistInput.id,
        user_ids: this.assignTherapistInput.userIds
      });
      this.assignTherapistLoading = false;
      if (!success) return;
      this.assignTherapistOpened = false;
    }
  }
};
</script>

<style scoped lang="scss">
.bl-clients-body {
  .bl-client {
    position: relative;
    width: 100%;
    height: 21.25rem;
    background: var(--background-depth-one-color);
    border: 1px solid var(--border-color);
    border-radius: 5px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    &:hover {
      border-color: var(--primary-color);
      .bl-client-body::after {
        background: var(--primary-color);
      }
    }
    .bl-client-header {
      position: relative;
      width: 100%;
      padding: 1rem;
      box-sizing: border-box;
      display: flex;
      align-items: center;
      gap: 1rem;
      .bl-client-header-avatar-container {
        position: relative;
      }
      .bl-client-header-detail-container {
        position: relative;
        width: calc(100% - 3.5rem);
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        .bl-client-header-name {
          position: relative;
          width: 100%;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .bl-client-header-birthday {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          .bl-client-header-birthday-icon {
            position: relative;
            width: 1rem;
            height: 1rem;
          }
        }
      }
    }
    .bl-client-body {
      position: relative;
      width: 100%;
      height: 6.5rem;
      padding: 0 1rem 1rem 1rem;
      box-sizing: border-box;
      display: flex;
      .bl-client-note {
        position: relative;
        width: 100%;
        height: 100%;
        opacity: 0.5;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        line-clamp: 4;
        -webkit-line-clamp: 4;
        -webkit-box-orient: vertical;
      }
      &::after {
        content: "";
        position: absolute;
        left: 0;
        bottom: 0;
        width: 100%;
        height: 1px;
        background: var(--border-color);
      }
    }
    .bl-client-footer {
      position: relative;
      width: 100%;
      height: 9.875rem;
      padding: 1rem;
      box-sizing: border-box;
      overflow-y: auto;
      .bl-client-tag {
        position: relative;
        max-width: 50%;
        height: 1.875rem;
        padding: 0 0.5rem;
        box-sizing: border-box;
        background: var(--grey-one);
        border-radius: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        .bl-client-tag-name {
          position: relative;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/clients/Clients.vue